import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2, FileText, MessageCircleQuestion, RotateCcw, Fingerprint, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { askVault, askVaultPrivate, fetchChatHistory, clearChatHistory, runChatAction, askVaultPhoto } from "../lib/api";
import { snippetsFor } from "../lib/localIndex";
import { usePrivateMode } from "./PrivateModeRow";
import { getHousehold } from "../lib/household";
import { getCategory } from "../lib/categories";
import { useSecurity } from "../context/SecurityContext";
import { useLang } from "../lib/i18n";
import { ReadAloudButton } from "./ReadAloud";
import { ActionCard } from "./chat/ActionCard";
import { ChatComposer } from "./chat/ChatComposer";
import { PhotoFileButton } from "./chat/PhotoFileButton";
import { TicketOffer } from "./chat/TicketOffer";
import { BriefingSettings } from "./chat/BriefingSettings";
import { MyBriefingSettings } from "./chat/MyBriefingSettings";

const SUGGESTIONS = {
  en: ["What needs my attention this week?", "What's my Wi-Fi password?", "When is my insurance renewal?", "Who is my executor?"],
  es: ["¿Qué necesita mi atención esta semana?", "¿Cuál es mi contraseña de Wi-Fi?", "¿Cuándo se renueva mi seguro?", "¿Quién es mi albacea?"],
};
const EXECUTOR_SUGGESTIONS = {
  en: ["I'm the executor — what do I do first?", "Where is the signed will stored?", "Which accounts need a death certificate?"],
  es: ["Soy el albacea — ¿qué hago primero?", "¿Dónde está el testamento firmado?", "¿Qué cuentas necesitan un certificado de defunción?"],
};

const WELCOME = {
  en: "Hi! Ask me anything about your documents — or ask me to set a reminder, tag a document, add a contact or draft a letter. Tap the mic to talk, or the camera to snap a bill and ask about it.",
  es: "¡Hola! Pregúntame lo que quieras sobre tus documentos — o pídeme un recordatorio, una etiqueta, un contacto o una carta. Toca el micrófono para hablar, o la cámara para fotografiar una factura.",
};
const welcomeMsg = (lang) => ({ role: "assistant", text: WELCOME[lang] || WELCOME.en, sources: [], welcome: true });

const localKey = (member) => `hv_private_chat:${getHousehold() || "primary"}:${member?.id || "x"}`;
const loadLocal = (member) => { try { return JSON.parse(localStorage.getItem(localKey(member)) || "[]"); } catch { return []; } };
const saveLocal = (member, msgs) => { try { localStorage.setItem(localKey(member), JSON.stringify(msgs.filter((m) => !m.welcome).slice(-24))); } catch {} };

export const AskVaultWidget = ({ onOpenDoc }) => {
  const navigate = useNavigate();
  const { member } = useSecurity();
  const { lang } = useLang();
  const privateMode = usePrivateMode();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = (e) => e.detail === "chat" && setOpen(true);
    window.addEventListener("hv:open", onOpen);
    return () => window.removeEventListener("hv:open", onOpen);
  }, []);
  const WELCOME = welcomeMsg(lang);
  const [messages, setMessages] = useState([WELCOME]);
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null); // {docs_considered, docs_total}
  const [briefingOpen, setBriefingOpen] = useState(false);
  const scrollRef = useRef(null);
  const isExecutor = member?.role === "executor";
  const suggestions = [...(isExecutor ? EXECUTOR_SUGGESTIONS[lang] || EXECUTOR_SUGGESTIONS.en : []), ...(SUGGESTIONS[lang] || SUGGESTIONS.en)].slice(0, 4);

  useEffect(() => {
    if (!open || loaded) return;
    if (privateMode) { const hist = loadLocal(member); if (hist.length) setMessages([WELCOME, ...hist]); setLoaded(true); return; }
    fetchChatHistory()
      .then((r) => {
        const hist = (r.messages || []).map((m) => ({ role: m.role, text: m.text, sources: m.sources || [], action: m.action || null, actionStatus: m.action ? "stale" : undefined }));
        if (hist.length) setMessages([WELCOME, ...hist]);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded, privateMode, member]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLoaded(false); }, [privateMode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      if (privateMode) {
        const snippets = await snippetsFor(question, 6);
        const history = messages.filter((m) => !m.welcome).slice(-8).map((m) => ({ role: m.role, text: m.text }));
        const res = await askVaultPrivate(question, snippets, history);
        const sources = snippets.filter((s) => res.source_ids.includes(s.id)).map((s) => ({ id: s.id, title: s.title, category: s.category }));
        setMessages((m) => { const next = [...m, { role: "assistant", text: res.answer, sources }]; saveLocal(member, next); return next; });
        setMeta({ docs_considered: res.docs_considered, docs_total: snippets.length, private: true });
        return;
      }
      const res = await askVault(question);
      setMessages((m) => [...m, { role: "assistant", text: res.answer, sources: res.sources || [], action: res.action || null, actionStatus: res.action ? "pending" : undefined, offerTicket: Boolean(res.offer_ticket) }]);
      setMeta({ docs_considered: res.docs_considered, docs_total: res.docs_total });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: e?.response?.data?.detail || "Sorry, I couldn't answer that right now.", sources: [] }]);
    } finally {
      setBusy(false);
    }
  };

  const setActionStatus = (idx, status, extra = {}) => setMessages((m) => m.map((x, i) => (i === idx ? { ...x, actionStatus: status, ...extra } : x)));
  const confirmAction = async (idx, action) => {
    try {
      const r = await runChatAction(action);
      setActionStatus(idx, "done");
      setMessages((m) => [...m, { role: "assistant", text: r.message, sources: r.source ? [r.source] : [] }]);
      toast.success(r.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't do that");
    }
  };

  const sendPhoto = async (file) => {
    if (busy) return;
    const question = input.trim();
    setInput("");
    const url = URL.createObjectURL(file);
    setMessages((m) => [...m, { role: "user", text: question || "What is this and should I keep it?", image: url }]);
    setBusy(true);
    try {
      const res = await askVaultPhoto(file, question);
      setMessages((m) => [...m, { role: "assistant", text: res.answer, sources: [], photo: { ...res, file } }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: e?.response?.data?.detail || "I couldn't read that photo right now.", sources: [] }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try {
      if (privateMode) localStorage.removeItem(localKey(member)); else await clearChatHistory();
      setMessages([WELCOME]);
      setMeta(null);
      toast.success("Started a new conversation");
    } catch {
      toast.error("Couldn't clear the conversation");
    }
  };

  const hasHistory = messages.length > 1;

  return (
    <>
      <motion.button
        data-testid="ask-vault-fab"
        onClick={() => setOpen((v) => !v)}
        style={open ? undefined : { display: "none" }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl grid place-items-center"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ask-vault-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[540px] max-h-[75vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border bg-primary/5">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground grid place-items-center">
                <MessageCircleQuestion className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg leading-none">Ask your vault</div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mt-1 truncate">
                  {privateMode ? <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="chat-private-badge"><Fingerprint className="h-3 w-3" /> Private · retrieved on this device</span> : member?.name ? `${member.name}'s conversation` : "AI · answers with sources"}
                </div>
              </div>
              {(member?.role === "owner" || member?.role === "admin" || member?.role === "partner") && (
                <button data-testid="chat-briefing-settings-btn" onClick={() => setBriefingOpen(true)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary rounded-full border border-border px-2.5 py-1" title="Sunday briefing by email or text">
                  <CalendarCheck className="h-3 w-3" /> Weekly
                </button>
              )}
              {hasHistory && (
                <button
                  data-testid="chat-new-btn"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary rounded-full border border-border px-2.5 py-1"
                  title="New conversation"
                >
                  <RotateCcw className="h-3 w-3" /> New
                </button>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={m.role === "user" ? "chat-user-msg" : "chat-assistant-msg"}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.image && <img src={m.image} alt="Snapped document" data-testid="chat-user-photo" className="mb-1.5 max-h-40 rounded-lg object-cover" />}
                    <p className="leading-relaxed whitespace-pre-wrap">{m.text}</p>
                    {m.role === "assistant" && !m.welcome && <ReadAloudButton text={m.text} testid={`chat-read-aloud-${i}`} className="mt-1" />}
                    {m.photo && <PhotoFileButton photo={m.photo} />}
                    {m.offerTicket && !privateMode && <TicketOffer lang={lang} />}
                    {m.action && m.actionStatus !== "stale" && (
                      <ActionCard action={m.action} status={m.actionStatus} onConfirm={() => confirmAction(i, m.action)} onDismiss={() => setActionStatus(i, "dismissed")} />
                    )}
                    {m.sources?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.sources.map((s) => {
                          const cat = getCategory(s.category);
                          return (
                            <button
                              key={s.id}
                              data-testid="chat-source-chip"
                              onClick={() => (s.page ? navigate(s.page) : onOpenDoc?.(s.id))}
                              className="inline-flex items-center gap-1 text-[11px] rounded-full bg-card border border-border px-2 py-0.5 hover:border-primary"
                            >
                              <FileText className="h-3 w-3" style={{ color: cat.iconColor }} />
                              <span className="truncate max-w-[140px]">{s.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              {!hasHistory && loaded && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {suggestions.map((s, idx) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => send(s)}
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-border bg-background px-3 py-1.5 hover:border-primary hover:text-primary transition-colors"
                    >
                      {idx === 0 && !isExecutor && <CalendarCheck className="h-3 w-3 text-primary" />}{s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {meta && (meta.docs_total > 0 || meta.private) && (
              <div data-testid="chat-docs-meta" className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-muted/40 border-t border-border">
                {meta.private ? `Sent ${meta.docs_considered} passage${meta.docs_considered === 1 ? "" : "s"} picked on this device — nothing else left the browser` : `Read ${meta.docs_considered} of ${meta.docs_total} documents for the last answer`}
              </div>
            )}

            <ChatComposer value={input} onChange={setInput} onSend={() => send()} onPhoto={sendPhoto} busy={busy} allowPhoto={!privateMode} placeholder={lang === "es" ? "Pregunta sobre tus documentos…" : "Ask about your documents…"} />
          </motion.div>
        )}
      </AnimatePresence>
      {member?.role === "partner" ? <MyBriefingSettings open={briefingOpen} onOpenChange={setBriefingOpen} /> : <BriefingSettings open={briefingOpen} onOpenChange={setBriefingOpen} />}
    </>
  );
};
